import styled from "@emotion/styled";
import {
  Box,
  Button,
  Callout,
  ContentToggle,
  EntityBadge,
  FlexRow,
  H3,
  Link,
  Stack,
  Text,
} from "atoms";
import { Thumbnail } from "features/images";
import { formatDate } from "utils/date";
import { useUsersReviews } from "../api/useUsersReviews";
import { Review } from "./Review";
import { ArtistReview, ProjectReview } from "../types";

type ProfileReviewsParams = {
  userId: string;
  showPublicDataOnly?: boolean;
};

function ProfileReviews({ userId, showPublicDataOnly }: ProfileReviewsParams) {
  const { data, hasNextPage, fetchNextPage, isFetching } = useUsersReviews({
    userId,
  });

  let allReviews: (ArtistReview | ProjectReview)[] = [];
  if (data?.pages.length) {
    allReviews = allReviews.concat(...data.pages.map((page) => page.data));
  }

  const loadMore = () => fetchNextPage();

  return (
    <>
      {allReviews.length === 0 ? (
        <Callout center>
          <H3 size="md">No reviews yet</H3>
          <Link size="sm" variant="primary" to="/search/">
            Explore to start writing reviews
          </Link>
        </Callout>
      ) : (
        <>
          <ReviewList gap="sm">
            {allReviews.map((review) => {
              if (
                (showPublicDataOnly && review.isAnonymous) ||
                (showPublicDataOnly && review.status !== "PUBLISHED")
              )
                return null;
              return (
                <ReviewSummary
                  status={review.status.toLowerCase()}
                  key={review.id}
                >
                  <FlexRow
                    alignItems="center"
                    justifyContent="space-between"
                    gap=".66rem"
                  >
                    <ReviewThumbnail
                      src={review.displayProjectAvatar}
                      entityType={review.reviewableType}
                      width="60"
                      height="60"
                    />

                    <FlexRow
                      alignItems="center"
                      gap=".66rem"
                      style={{ flex: 1 }}
                    >
                      <ReviewTitle gap="xs">
                        <H3 size="sm">
                          <FlexRow gap="8px">
                            <Link
                              to={`/${review.reviewableType.toLowerCase()}s/${
                                review.reviewableId
                              }`}
                            >
                              {review.displayProjectName}
                            </Link>{" "}
                            <EntityBadge entityType={review.reviewableType} />
                          </FlexRow>
                        </H3>

                        <Text size="xxxs">
                          Created on{" "}
                          {formatDate(review.createdAt, {
                            month: "long",
                            day: "numeric",
                            year: "numeric",
                          })}
                          <br />
                          {review.updatedAt &&
                            `Updated on ${formatDate(review.updatedAt, {
                              month: "long",
                              day: "numeric",
                              year: "numeric",
                            })}`}
                        </Text>
                      </ReviewTitle>

                      <StatusRow>
                        <Status size="xxs">
                          {review.status.toLowerCase()}
                        </Status>
                      </StatusRow>
                    </FlexRow>
                  </FlexRow>

                  <ContentToggle
                    showLabel="Show review"
                    hideLabel="Hide review"
                  >
                    <SReview
                      key={review.reviewId}
                      review={review}
                      showVotes={false}
                    />
                  </ContentToggle>
                </ReviewSummary>
              );
            })}
          </ReviewList>

          {hasNextPage && (
            <SButton variant="primary" onClick={loadMore} disabled={isFetching}>
              Load More...
            </SButton>
          )}
        </>
      )}
    </>
  );
}

const SButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing.sm,
}));

const ReviewList = styled(Stack)({
  listStyleType: "none",
  margin: "0 0 1rem",
  padding: 0,
  "&>*::before": {
    content: '"\\200B"',
    position: "absolute",
  },
}).withComponent("ul");

type ReviewSummaryProps = {
  status?: string;
};

const ReviewSummary = styled(Box)<ReviewSummaryProps>(({ status, theme }) => ({
  alignItems: "stretch",
  display: "flex",
  flexDirection: "column",
  gap: ".5em",
  justifyContent: "center",
  padding: "1rem",
  "&>*": {
    flex: "0 0 100%",
  },
  "&>h3": {
    margin: 0,
  },
  p: {
    margin: 0,
  },
})).withComponent("li");
ReviewSummary.defaultProps = { className: "ReviewSummary" };

const ReviewThumbnail = styled(Thumbnail)({
  width: 60,
  height: 60,
  flex: "0 0 60px",
});

const ReviewTitle = styled(Stack)({
  flex: "1 0 225px",
  "&>*": {
    flex: "1 0 200px",
  },
});

const StatusRow = styled(FlexRow)({
  gap: ".5rem 1rem",
  flexWrap: "nowrap",
  "@media (min-width: 474px)": {
    justifyContent: "flex-end",
    flexDirection: "column",
    alignItems: "flex-start",
  },
});

const Status = styled(Text)({
  textTransform: "capitalize",
  fontWeight: "bold",
});

const SReview = styled(Review)({
  paddingTop: 0,
});

export { ProfileReviews, ReviewSummary, Status };
